import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  servingApi,
  type AssessmentItem,
  type ChapterStandards,
  type Lang,
  type Materials,
  type Scene,
  type SceneTiming,
  type TeacherAssessmentGroup,
  type Tier,
} from "../../../api/serving";
import { fetchVocabLaunch, type VocabLaunchCard } from "../../../api/peContent";
import { MaterialDocPanel, ChecklistPanel, FamilyLetterPanel } from "./shared/MaterialDocs";
import { chassis } from "./shared/chassis";
import { peT, type PeLessonStrings } from "../../../i18n/peLesson";
import LanguageToggle from "./shared/LanguageToggle";
import { useChapterNumber } from "./shared/useChapterNumber";

export interface SceneReaderProps {
  chapterId: string;
  tier: Tier;
  /** The per-assignment language setting from the LTI launch. */
  lang: Lang;
  role: "student" | "teacher";
  /**
   * When true, drop the full-page min-h-screen + background wrapper so the
   * reader nests inside an outer shell (e.g. the teacher lesson-preview page,
   * which provides the dashboard chrome + gray-50 page background).
   */
  embedded?: boolean;
}

/**
 * Teacher doc-hub tabs. Canonical 5 (guide · quickref · readaloud · checklist ·
 * standards) + the pre-existing Family Letter and Assessment planes.
 */
type DocTab =
  | "guide"
  | "quickref"
  | "vocab_launch"
  | "readaloud"
  | "checklist"
  | "standards"
  | "family"
  | "assessment";

/** Stable-ish scene ordinal for sorting (falls back to string compare). */
const sceneOrd = (s: string): number => {
  const m = String(s).match(/(\d+)\s*$/);
  return m ? Number(m[1]) : Number.MAX_SAFE_INTEGER;
};

/**
 * Client-side reshape (decided — no backend edit): bucket formative items
 * (/pe/assessments) and summative items (/pe/quiz, T3/T4) into teacher-facing
 * groups, ordered by scene. Answer keys are withheld by the serving layer, so
 * nothing here marks a correct answer (see // TODO(keys) at the render site).
 */
function buildTeacherAssessmentGroups(
  formative: AssessmentItem[],
  summative: AssessmentItem[],
  t: PeLessonStrings,
): TeacherAssessmentGroup[] {
  const byScene = (a: AssessmentItem, b: AssessmentItem) =>
    sceneOrd(a.scene_id) - sceneOrd(b.scene_id);
  const groups: TeacherAssessmentGroup[] = [];
  if (formative.length)
    groups.push({ kind: "formative", label: t.formativeItems, items: [...formative].sort(byScene) });
  if (summative.length)
    groups.push({ kind: "summative", label: t.summativeQuiz, items: [...summative].sort(byScene) });
  return groups;
}

/**
 * Scene Reader — T1 audio-first read-along.
 *
 * Each scene shows its comic still + student prose + a play button. Scenes
 * play a per-scene narration MP3 (EN and ES) and karaoke-highlight each word
 * using the per-tier timing JSON. When a scene's audio ends we AUTO-ADVANCE to
 * the next scene and keep playing — the target audience is K–1 non-readers, for
 * whom a hands-free continuous read-along is the right experience; tapping any
 * scene's play button still lets them jump or re-listen.
 *
 * No grading, no submit — this is a read-only reading surface.
 */
const SceneReader: React.FC<SceneReaderProps> = ({ chapterId, tier, lang, role, embedded }) => {
  const chapterNumber = useChapterNumber(chapterId);
  const [displayLang, setDisplayLang] = useState<Lang>(lang);
  // Run E2 J5 (D04, D20): reader + teacher-materials strings follow the
  // language on screen (the assignment language until the toggle is used).
  const t = peT(displayLang);
  const printHeading = t.materialsRail.printed(`${t.chapter} ${chapterNumber ?? ""}`.trim(), t.tier(tier), displayLang.toUpperCase());
  const [scenes, setScenes] = useState<Scene[]>([]);
  const [timing, setTiming] = useState<SceneTiming | null>(null);
  const [materials, setMaterials] = useState<Materials | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Teacher doc-hub tab strip + lazily-loaded Assessment tab state.
  const [docTab, setDocTab] = useState<DocTab | null>(null);
  const [assessment, setAssessment] = useState<TeacherAssessmentGroup[] | null>(null);
  const [assessmentLoading, setAssessmentLoading] = useState(false);
  const [assessmentError, setAssessmentError] = useState<string | null>(null);
  // The content key we've already fetched assessments for (lazy-load guard).
  const loadedAsmtKeyRef = useRef<string | null>(null);

  // Lazily-loaded Standards tab state (fetched from /pe/chapters/standards).
  const [standards, setStandards] = useState<ChapterStandards | null>(null);
  const [standardsLoading, setStandardsLoading] = useState(false);
  const [standardsError, setStandardsError] = useState<string | null>(null);
  const loadedStdChapterRef = useRef<string | null>(null);

  // Lazily-loaded Vocab Launch tab (teacher-only; role='launch', all tiers).
  const [launch, setLaunch] = useState<VocabLaunchCard[] | null>(null);
  const [launchLoading, setLaunchLoading] = useState(false);
  const [launchError, setLaunchError] = useState<string | null>(null);
  const loadedLaunchKeyRef = useRef<string | null>(null);

  // Active-audio state. currentIdx = the scene whose MP3 is loaded; wordIdx =
  // the karaoke word index within that scene (-1 = none highlighted yet).
  const [currentIdx, setCurrentIdx] = useState<number | null>(null);
  const [playing, setPlaying] = useState(false);
  const [wordIdx, setWordIdx] = useState(-1);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Prose is single-language now, so the toggle DOES refetch (unlike the old
  // both-languages payload).
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    // Stop any in-flight playback before swapping content (e.g. EN→ES toggle).
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.removeAttribute("src");
    }
    setCurrentIdx(null);
    setPlaying(false);
    setWordIdx(-1);
    setTiming(null);
    (async () => {
      try {
        const res = await servingApi.getScenes({ chapterId, tier, language: displayLang });
        if (cancelled) return;
        setScenes(res.scenes);
        setError(null);
        // Fetch the per-tier timing JSON (EN chapter-absolute; ES scene-local start_time=0).
        if (res.timing_url) {
          fetch(res.timing_url)
            .then((r) => (r.ok ? r.json() : null))
            .then((t: SceneTiming | null) => { if (!cancelled) setTiming(t); })
            .catch(() => { /* karaoke degrades to plain prose */ });
        }
      } catch (e) {
        console.warn("[SceneReader] scenes load failed:", e);
        if (!cancelled) setError("load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [chapterId, tier, displayLang]);

  // Teacher materials follow the current display language.
  useEffect(() => {
    if (role !== "teacher") return;
    let cancelled = false;
    servingApi
      .getMaterials({ chapterId, language: displayLang })
      .then((m) => { if (!cancelled) setMaterials(m); })
      .catch(() => { /* materials are non-blocking for the reader */ });
    return () => { cancelled = true; };
  }, [chapterId, role, displayLang]);

  // Assessment tab — lazy fetch on first open per content key. Formative items
  // come from /pe/assessments (step 3); summative (T3/T4 only) is a best-effort
  // /pe/quiz that never blocks the formative group. Failures are surfaced in the
  // panel, never crash the aside.
  const asmtKey = `${chapterId}:${tier}:${displayLang}`;
  useEffect(() => {
    if (role !== "teacher" || docTab !== "assessment") return;
    if (loadedAsmtKeyRef.current === asmtKey) return; // already loaded this key
    loadedAsmtKeyRef.current = asmtKey;
    let cancelled = false;
    setAssessment(null);
    setAssessmentError(null);
    setAssessmentLoading(true);
    (async () => {
      try {
        const formative = await servingApi.getAssessments({ chapterId, tier, language: displayLang });
        let summativeItems: AssessmentItem[] = [];
        if (tier === "T3" || tier === "T4") {
          try {
            const quiz = await servingApi.getQuiz({ chapterId, tier, language: displayLang });
            summativeItems = quiz.items;
          } catch { /* summative is optional; never block the formative group */ }
        }
        if (cancelled) return;
        setAssessment(buildTeacherAssessmentGroups(formative.items, summativeItems, peT(displayLang)));
      } catch (e) {
        if (cancelled) return;
        console.warn("[SceneReader] assessments load failed:", e);
        setAssessmentError("load");
        loadedAsmtKeyRef.current = null; // allow retry on reopen
      } finally {
        if (!cancelled) setAssessmentLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [role, docTab, asmtKey, chapterId, tier, displayLang]);

  // Standards tab — lazy fetch on first open per chapter (chapter-scoped, not
  // tier/lang). Failures surface in the panel; never crash the aside.
  useEffect(() => {
    if (role !== "teacher" || docTab !== "standards") return;
    if (loadedStdChapterRef.current === chapterId) return;
    loadedStdChapterRef.current = chapterId;
    let cancelled = false;
    setStandards(null);
    setStandardsError(null);
    setStandardsLoading(true);
    servingApi
      .getChapterStandards(chapterId)
      .then((s) => { if (!cancelled) setStandards(s); })
      .catch((e) => {
        if (cancelled) return;
        console.warn("[SceneReader] standards load failed:", e);
        setStandardsError("load");
        loadedStdChapterRef.current = null; // allow retry on reopen
      })
      .finally(() => { if (!cancelled) setStandardsLoading(false); });
    return () => { cancelled = true; };
  }, [role, docTab, chapterId]);

  // Vocab Launch tab — lazy fetch on first open per chapter+language.
  const launchKey = `${chapterId}:${displayLang}`;
  useEffect(() => {
    if (role !== "teacher" || docTab !== "vocab_launch") return;
    if (loadedLaunchKeyRef.current === launchKey) return;
    loadedLaunchKeyRef.current = launchKey;
    let cancelled = false;
    setLaunch(null);
    setLaunchError(null);
    setLaunchLoading(true);
    fetchVocabLaunch(chapterId, displayLang)
      .then((c) => { if (!cancelled) setLaunch(c); })
      .catch((e) => {
        if (cancelled) return;
        console.warn("[SceneReader] launch cards load failed:", e);
        setLaunchError("load");
        loadedLaunchKeyRef.current = null; // allow retry on reopen
      })
      .finally(() => { if (!cancelled) setLaunchLoading(false); });
    return () => { cancelled = true; };
  }, [role, docTab, launchKey, chapterId, displayLang]);

  // Per-scene timing, indexed by scene_number, with word times normalized to
  // the scene's own audio file (abs - start_time).
  const localTimingByScene = useMemo(() => {
    const map = new Map<number, { start: number; end: number; word: string }[]>();
    if (!timing) return map;
    for (const sc of timing.scenes ?? []) {
      map.set(
        sc.scene_number,
        (sc.words ?? []).map((w) => ({
          word: w.word,
          start: Math.max(0, w.start - sc.start_time),
          end: Math.max(0, w.end - sc.start_time),
        })),
      );
    }
    return map;
  }, [timing]);

  // Load + play whenever the active scene changes.
  useEffect(() => {
    const a = audioRef.current;
    if (!a || currentIdx == null) return;
    const url = scenes[currentIdx]?.audio_url;
    if (!url) return;
    a.src = url;
    a.currentTime = 0;
    setWordIdx(-1);
    a.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
  }, [currentIdx, scenes]);

  const playScene = (i: number) => {
    const a = audioRef.current;
    if (!a || !scenes[i]?.audio_url) return;
    if (currentIdx === i) {
      // Toggle pause/resume on the active scene.
      if (a.paused) a.play().then(() => setPlaying(true)).catch(() => {});
      else { a.pause(); setPlaying(false); }
      return;
    }
    setCurrentIdx(i);
  };

  const onTimeUpdate = () => {
    const a = audioRef.current;
    if (!a || currentIdx == null) return;
    const words = localTimingByScene.get(scenes[currentIdx]?.scene_number);
    if (!words || !words.length) return;
    const t = a.currentTime;
    let idx = -1;
    for (let i = 0; i < words.length; i++) {
      if (words[i].start <= t + 0.01) idx = i;
      else break;
    }
    if (idx !== wordIdx) setWordIdx(idx);
  };

  const onEnded = () => {
    setWordIdx(-1);
    // Auto-advance to the next scene that has audio.
    let next = (currentIdx ?? -1) + 1;
    while (next < scenes.length && !scenes[next]?.audio_url) next++;
    if (next < scenes.length) setCurrentIdx(next);
    else { setPlaying(false); setCurrentIdx(null); }
  };

  // Run E2 J5 round 2: Standards tab text in the shown language. pe_chapters holds
  // English only; the Spanish lives in the ES teacher guide row
  // (content_json._standards_es: where-addressed notes + CEE/Jump$tart texts) and,
  // for the fixed CCSS statements, in the string table. Matched by the English text,
  // so a later DB edit falls back to English instead of showing stale Spanish.
  const standardsEs: Array<{ code: string; code_es?: string; standard_en: string; where_en: string; standard: string | null; where_addressed: string }> =
    displayLang === "es" && Array.isArray((materials?.teacher_guide?.content_json as any)?._standards_es)
      ? (materials!.teacher_guide!.content_json as any)._standards_es
      : [];
  const stdText = (st: { code: string; standard: string; where_addressed?: string | null }) => {
    const hit = standardsEs.find((e) => e.standard_en === st.standard && e.where_en === st.where_addressed);
    return {
      code: hit?.code === st.code && hit?.code_es ? hit.code_es : st.code,
      standard: hit?.standard ?? t.standardText(st.standard),
      where: hit?.where_addressed ?? st.where_addressed ?? "",
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: chassis.background }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: chassis.accent }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: chassis.background }}>
        <p className="text-red-600 font-medium px-6 text-center">{t.loadScenesFailed}</p>
      </div>
    );
  }

  const renderProse = (s: Scene, i: number) => {
    const words = localTimingByScene.get(s.scene_number);
    // Karaoke for the active scene when timing words are available (EN + ES).
    if (currentIdx === i && words && words.length) {
      return (
        <p className="leading-relaxed text-lg" style={{ color: chassis.textPrimary }}>
          {words.map((w, wi) => (
            <span
              key={wi}
              style={
                wi === wordIdx
                  ? { backgroundColor: chassis.accent, color: chassis.accentText, borderRadius: 4, padding: "0 2px" }
                  : wi < wordIdx
                  ? { color: chassis.textSecondary }
                  : { color: chassis.textPrimary }
              }
            >
              {w.word}{" "}
            </span>
          ))}
        </p>
      );
    }
    return (
      <p className="leading-relaxed text-lg whitespace-pre-line" style={{ color: chassis.textPrimary }}>
        {s.prose ?? ""}
      </p>
    );
  };

  // The scene list (play-all row + scene cards). Rendered in the right column
  // for teachers (default view) and as the whole body for students — defined
  // once here so it isn't duplicated across the two layouts.
  const scenesView = (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm" style={{ color: chassis.textSecondary }}>
          {t.scenesCount(scenes.length)} · {t.langName[displayLang]}
        </p>
        <button
          type="button"
          onClick={() => playScene(0)}
          className="inline-flex items-center gap-2 text-sm font-semibold rounded-full px-4 py-1.5 transition-colors"
          style={{ backgroundColor: chassis.accent, color: chassis.accentText }}
        >
          {t.playStory}
        </button>
      </div>
      {scenes.map((s, i) => {
        const active = currentIdx === i;
        return (
          <div
            key={s.scene_code}
            className="bg-white rounded-xl border p-4"
            style={{
              borderColor: active ? chassis.accent : "rgb(229 231 235)",
              boxShadow: active ? `0 0 0 2px ${chassis.accent}33` : undefined,
            }}
          >
            <div className="flex items-center gap-3 mb-3">
              <div
                className="flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center text-sm font-bold"
                style={{ backgroundColor: chassis.accent, color: chassis.accentText }}
              >
                {s.scene_number}
              </div>
              <button
                type="button"
                onClick={() => playScene(i)}
                disabled={!s.audio_url}
                aria-label={active && playing ? t.pauseScene(s.scene_number) : t.playScene(s.scene_number)}
                className="inline-flex items-center gap-2 text-sm font-semibold rounded-full px-4 py-1.5 transition-colors disabled:opacity-40"
                style={{
                  backgroundColor: active ? chassis.accent : "transparent",
                  color: active ? chassis.accentText : chassis.accent,
                  border: `1px solid ${chassis.accent}`,
                }}
              >
                {active && playing ? t.pause : t.play}
              </button>
            </div>

            {s.image_url && (
              <img
                src={s.image_url}
                alt={t.sceneAlt(s.scene_number)}
                className="rounded-lg mb-3 w-full object-contain max-h-96 bg-gray-50"
              />
            )}
            {renderProse(s, i)}
          </div>
        );
      })}
    </div>
  );

  return (
    <div
      className={embedded ? "" : "min-h-screen"}
      style={embedded ? undefined : { background: chassis.background }}
    >
      {/* One shared audio element drives all scenes. */}
      <audio
        ref={audioRef}
        preload="none"
        onTimeUpdate={onTimeUpdate}
        onEnded={onEnded}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
      />

      <div
        className={
          embedded
            ? role === "teacher"
              ? "w-full" // teacher preview: fill the page for the rail + content split
              : "max-w-5xl mx-auto"
            : "max-w-3xl mx-auto px-4 py-8"
        }
      >
        <div className="flex items-start justify-between gap-4 mb-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: chassis.icon }}>
              {t.chapter} {chapterNumber ?? "…"} · {t.tier(tier)}
            </p>
            <h1 className="text-2xl font-bold" style={{ color: chassis.textPrimary }}>
              {t.sceneReader}
            </h1>
          </div>
          <LanguageToggle value={displayLang} onChange={setDisplayLang} assignmentDefault={lang} t={t} />
        </div>

        {role === "teacher" ? (
          <div className="grid grid-cols-1 lg:grid-cols-[15rem_minmax(0,1fr)] gap-6 items-start">
            {/* Page-level sticky LEFT rail — the Teacher Materials tabs. Stays
                visible while the right column scrolls. */}
            <aside className="lg:sticky lg:top-[88px] self-start bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <p className="px-4 pt-4 pb-2 text-xs font-bold uppercase tracking-wider text-gray-500">
                {t.teacherMaterials}
              </p>
              <ul className="flex lg:flex-col overflow-x-auto lg:overflow-visible lg:pb-3">
                {(
                  [
                    "guide",
                    "quickref",
                    "vocab_launch",
                    "readaloud",
                    "checklist",
                    "standards",
                    "family",
                    "assessment",
                  ] as DocTab[]
                ).map((key) => {
                  const label = t.tabs[key];
                  const isActive = docTab === key;
                  return (
                    <li key={key} className="shrink-0">
                      <button
                        type="button"
                        onClick={() => setDocTab(key)}
                        aria-current={isActive ? "page" : undefined}
                        className={`w-full text-left whitespace-nowrap px-4 py-2 text-sm font-medium border-l-4 transition-colors ${
                          isActive
                            ? "bg-indigo-50 text-indigo-700 border-indigo-600 lg:rounded-r-lg"
                            : "border-transparent text-gray-600 hover:bg-gray-100"
                        }`}
                      >
                        {label}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </aside>

            {/* RIGHT column — scenes by default; a selected material replaces
                them at full width, with a clear way back. */}
            <div className="min-w-0">
              {docTab === null ? (
                scenesView
              ) : (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 md:p-6">
                  <button
                    type="button"
                    onClick={() => setDocTab(null)}
                    className="inline-flex items-center gap-1 text-sm font-medium text-indigo-600 hover:text-indigo-700 mb-4"
                  >
                    {t.backToLesson}
                  </button>

                {/* Teacher Guide tab — structured doc, rendered generically */}
                {docTab === "guide" && (
                  <MaterialDocPanel doc={materials?.teacher_guide} empty={t.noDoc.guide} lang={displayLang} tier={tier} printHeading={printHeading} showPrintButton />
                )}

                {/* Quick Reference tab */}
                {docTab === "quickref" && (
                  <MaterialDocPanel doc={materials?.quick_reference} empty={t.noDoc.quickref} lang={displayLang} tier={tier} printHeading={printHeading} showPrintButton />
                )}

                {/* Vocab Launch tab — teacher-only launch cards (role='launch'), all tiers */}
                {docTab === "vocab_launch" && (
                  <section>
                    {launchLoading ? (
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <span className="animate-spin inline-block h-4 w-4 rounded-full border-b-2 border-indigo-500" />
                        {t.loadingLaunch}
                      </div>
                    ) : launchError ? (
                      <p className="text-sm text-red-600">{t.loadLaunchFailed}</p>
                    ) : !launch || launch.length === 0 ? (
                      <p className="text-sm text-gray-500">{t.noLaunch}</p>
                    ) : (
                      <div className="space-y-5">
                        {(["T1", "T2", "T3", "T4"] as const)
                          .map((t) => ({ t, cards: launch.filter((c) => c.tier === t) }))
                          .filter((g) => g.cards.length > 0)
                          .map((g) => (
                            <div key={g.t}>
                              <h3 className="text-sm font-semibold text-gray-900 mb-2">
                                {t.tier(g.t)}{" "}
                                <span className="text-xs font-normal text-gray-400">({g.cards.length})</span>
                              </h3>
                              <ul className="space-y-2">
                                {g.cards.map((c) => (
                                  <li key={c.id} className="rounded-md border border-gray-200 p-3">
                                    <div className="flex items-start gap-3">
                                      {c.imageUrl && (
                                        <img
                                          src={c.imageUrl}
                                          alt={c.word}
                                          className="h-12 w-12 rounded object-cover bg-gray-50 shrink-0"
                                        />
                                      )}
                                      <div className="min-w-0">
                                        <p className="text-sm font-semibold text-gray-900">{c.word}</p>
                                        {c.definition && (
                                          <p className="text-sm text-gray-700 mt-0.5">{c.definition}</p>
                                        )}
                                        {c.example && (
                                          <p className="text-xs text-gray-500 mt-1 italic">“{c.example}”</p>
                                        )}
                                      </div>
                                    </div>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                      </div>
                    )}
                  </section>
                )}

                {/* Read-Aloud (K–1) tab — not yet seeded → empty-state */}
                {docTab === "readaloud" && (
                  <MaterialDocPanel doc={materials?.read_aloud_script} empty={t.noDoc.readaloud} lang={displayLang} tier={tier} printHeading={printHeading} showPrintButton />
                )}

                {/* Standards tab — frameworks from /pe/chapters/standards */}
                {docTab === "standards" && (
                  <section>
                    {standardsLoading ? (
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <span className="animate-spin inline-block h-4 w-4 rounded-full border-b-2 border-indigo-500" />
                        {t.loadingStandards}
                      </div>
                    ) : standardsError ? (
                      <p className="text-sm text-red-600">{t.loadStandardsFailed}</p>
                    ) : !standards || standards.frameworks.length === 0 ? (
                      <p className="text-sm text-gray-500">{t.noStandards}</p>
                    ) : (
                      <div className="space-y-5">
                        {standards.frameworks.map((fw, fi) => (
                          <div key={fi}>
                            <h3 className="text-sm font-semibold text-gray-900 mb-2">
                              {t.frameworkName(fw.framework)}{" "}
                              <span className="text-xs font-normal text-gray-400">
                                ({fw.standards.length})
                              </span>
                            </h3>
                            <ul className="space-y-2">
                              {fw.standards.map((st, si) => (
                                <li
                                  key={si}
                                  className="rounded-md border border-gray-200 p-2.5"
                                >
                                  <p className="text-xs font-mono font-semibold text-indigo-700">
                                    {stdText(st).code}
                                  </p>
                                  <p className="text-sm text-gray-800 mt-0.5">{stdText(st).standard}</p>
                                  {st.where_addressed && (
                                    <p className="text-xs text-gray-500 mt-1">
                                      <span className="font-medium">{t.where}</span>{" "}
                                      {stdText(st).where}
                                    </p>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    )}
                  </section>
                )}

                {/* Observation Checklist tab */}
                {docTab === "checklist" && (
                  <ChecklistPanel
                    checklist={materials?.observation_checklist}
                    empty={t.noChecklist}
                    lang={displayLang}
                    printHeading={printHeading}
                    showPrintButton
                  />
                )}

                {/* Family Letter tab */}
                {docTab === "family" && (
                  <FamilyLetterPanel
                    letter={materials?.family_letter}
                    empty={t.noFamilyLetter}
                    lang={displayLang}
                    printHeading={printHeading}
                    showPrintButton
                  />
                )}

                {/* Assessment tab — formative + summative, teacher-facing, no keys */}
                {docTab === "assessment" && (
                  <section>
                    {assessmentLoading ? (
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <span className="animate-spin inline-block h-4 w-4 rounded-full border-b-2 border-indigo-500" />
                        {t.loadingAssessment}
                      </div>
                    ) : assessmentError ? (
                      <p className="text-sm text-red-600">{t.loadAssessmentFailed}</p>
                    ) : !assessment || assessment.length === 0 ? (
                      <p className="text-sm text-gray-500">{t.noAssessment}</p>
                    ) : (
                      <div className="space-y-5">
                        {assessment.map((g) => (
                          <div key={g.kind}>
                            <h3 className="text-sm font-semibold text-gray-900 mb-2">
                              {g.kind === "summative" ? t.summativeQuiz : t.formativeItems}{" "}
                              <span className="text-xs font-normal text-gray-400">
                                ({g.items.length})
                              </span>
                            </h3>
                            <ol className="space-y-3">
                              {g.items.map((it, idx) => (
                                <li
                                  key={it.item_id}
                                  className="text-sm rounded-md border border-gray-200 p-3 text-gray-700"
                                >
                                  <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
                                    <span className="text-xs font-bold text-indigo-600">
                                      {idx + 1}.
                                    </span>
                                    <span className="text-[10px] uppercase tracking-wide font-semibold rounded px-1.5 py-0.5 bg-indigo-50 text-indigo-700">
                                      {it.type === "audio_match" && it.response_mode === "audio"
                                        ? t.itemKind.audio
                                        : t.itemKind[it.type as keyof PeLessonStrings["itemKind"]] ?? it.type}
                                    </span>
                                    <span className="text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5 bg-gray-100 text-gray-500">
                                      {it.response_mode === "audio" ? t.teacherReviewed : t.autoScored}
                                    </span>
                                    {it.scene_id != null && String(it.scene_id) !== "" && (
                                      <span className="text-[10px] text-gray-400">{t.sceneRef(it.scene_id)}</span>
                                    )}
                                  </div>
                                  <p className="mb-1.5 font-medium text-gray-900">
                                    {it.prompt}
                                  </p>
                                  {/* TODO(keys): the serving layer WITHHOLDS correct answers
                                      (choices arrive shuffled, key merged-in-but-unlabeled), so
                                      options below render WITHOUT correct-answer marking. Restore
                                      key display only behind an authorized teacher endpoint. */}
                                  {it.choices && it.choices.length > 0 && (
                                    <ul className="space-y-0.5 ml-1">
                                      {it.choices.map((c) => (
                                        <li key={c.id} className="flex gap-1.5">
                                          <span className="text-gray-400">{c.id})</span>
                                          <span>
                                            {/* T1 picture choices carry the asset key as text: show the picture
                                                (Run E3 D30), never the file path. */}
                                            {c.image_url && (!c.text || /\.(png|jpe?g|webp)$/i.test(c.text)) ? (
                                              <img
                                                src={c.image_url}
                                                alt={t.picture(it.choices!.indexOf(c) + 1)}
                                                loading="lazy"
                                                className="h-16 w-auto max-w-[8rem] rounded border border-gray-200 object-cover"
                                              />
                                            ) : (
                                              c.text || "—"
                                            )}
                                          </span>
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                  {it.word_bank && it.word_bank.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mt-1">
                                      {it.word_bank.map((w, wi) => (
                                        <span key={wi} className="text-xs rounded px-1.5 py-0.5 bg-gray-100 text-gray-600">
                                          {w}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                  {it.drag_items && it.drag_items.length > 0 && (
                                    <p className="text-xs mt-1 text-gray-500">
                                      {/* Picture tiles show "Picture N", order slots "Position N"
                                          in the doc language, never the asset key (Run E2 J5). */}
                                      {t.dragItems}{" "}
                                      {it.drag_items.map((d, di) => {
                                        const img = it.drag_item_images?.[di];
                                        return (
                                          <React.Fragment key={di}>
                                            {di > 0 ? ", " : ""}
                                            {img ? (
                                              <img
                                                src={img}
                                                alt={t.picture(di + 1)}
                                                loading="lazy"
                                                className="inline-block h-12 w-auto max-w-[6rem] align-middle rounded border border-gray-200 object-cover"
                                              />
                                            ) : (
                                              d
                                            )}
                                          </React.Fragment>
                                        );
                                      })}
                                      {it.drag_targets && it.drag_targets.length
                                        ? ` → ${it.drag_targets
                                            .map((x) => {
                                              const m = /^Position (\d+)$/.exec(x);
                                              return m ? t.position(Number(m[1])) : x;
                                            })
                                            .join(", ")}`
                                        : ""}
                                    </p>
                                  )}
                                  {it.audio_prompt_url && (
                                    <audio
                                      controls
                                      preload="none"
                                      src={it.audio_prompt_url}
                                      className="mt-1.5 w-full h-8"
                                    />
                                  )}
                                </li>
                              ))}
                            </ol>
                          </div>
                        ))}
                      </div>
                    )}
                  </section>
                )}
                </div>
              )}
            </div>
          </div>
        ) : (
          scenesView
        )}
      </div>
    </div>
  );
};

export default SceneReader;
